import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useSteps from '../StepContext/useSteps';
import { Button, Spinner } from 'reactstrap';
import { useFormikContext } from 'formik';
import { dietSettings } from '../types';
import { useToast } from '@front-activerse/common-components';
import createRequest from '../helpers/createRequest';
import { useTranslator, useAppContext } from '@front-activerse/common-components';

const NextButton: React.FC = () => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const {
    nextStep,
    currentMainStep,
    currentStep,
    shouldBackendValidate,
    validateDietSettings,
    dietTypes,
    dietLevels,
  } = useSteps();
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    consts: { SLIDE_ROUTES_DURATION, APP_BRAND },
  } = useAppContext();

  const { values, errors, setFieldError, setFieldTouched } = useFormikContext<dietSettings>();
  const navigate = useNavigate();

  // Effect to control button disabled state
  useEffect(() => {
    const checkDisabledState = () => {
      // Check if there are form validation errors for current step
      const hasCurrentStepError = Boolean(errors[currentStep as keyof dietSettings]);

      // Check if required value is missing for current step
      const currentValue = values[currentStep as keyof dietSettings];
      const isMissingRequiredValue =
        !currentValue || (typeof currentValue === 'number' && currentValue <= 0);

      // Button should be disabled if there are errors, missing values, or loading
      setIsDisabled(loading || hasCurrentStepError || isMissingRequiredValue);
    };

    checkDisabledState();
  }, [currentStep, values, errors, loading]);

  const validate = async () => {
    let isError = false;

    if (shouldBackendValidate) {
      try {
        setLoading(true);

        const validationResult: any = await validateDietSettings(
          createRequest(values, dietTypes, dietLevels, String(APP_BRAND))
        );
        const currentMainStepErrors = validationResult.details;

        let currentStepError: any = '';

        if (currentMainStep === 'goalSettings' || currentMainStep === 'all') {
          if (currentStep === 'weightValue') {
            currentStepError = currentMainStepErrors.find(
              (err: any) => err.fieldName === 'weight.value'
            );
          } else if (currentStep === 'goalWeightValue') {
            currentStepError = currentMainStepErrors.find(
              (err: any) => err.fieldName === 'goalWeight.value'
            );

            if (currentStepError) currentStepError.fieldName = 'goalWeightValue';
          } else if (currentStep === 'dateOfBirth') {
            currentStepError = currentStepError = currentMainStepErrors.find(
              (err: any) => err.fieldName === currentStep
            );
          }
        } else if (currentMainStep === 'biometric' || currentMainStep === 'all') {
          if (currentStep === 'dateOfBirth') {
            currentStepError = currentStepError = currentMainStepErrors.find(
              (err: any) => err.fieldName === currentStep
            );
          }

          if (currentStep === 'heightValue') {
            currentStepError = currentMainStepErrors.find(
              (err: any) => err.fieldName === 'height.value'
            );
          }
        }

        if (currentStepError) {
          setFieldError(currentStep, currentStepError.messages[0]);
          setFieldTouched(currentStep, true, false);

          isError = true;
        }
      } catch (err) {
        console.log('err', err);
        toastError();
        isError = true;
      } finally {
        setLoading(false);
      }
    }
    return isError;
  };

  const handleClick = async () => {
    // Blur the button to remove focus
    buttonRef.current?.blur();

    const isError = await validate();

    if (!isError) {
      if (nextStep) {
        //navigate to the next step
        scrollToTopAndNavigate(`/diet-settings/${currentMainStep}/${nextStep}`);
      }
    }
  };

  const scrollToTopAndNavigate = (url: string) => {
    const el = document.querySelector('.layout') as HTMLInputElement;
    if (el.scrollTop) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate(url);
      }, +SLIDE_ROUTES_DURATION);
    } else {
      navigate(url);
    }
  };

  return (
    <Button
      color="primary"
      className={`w-100 ${isDisabled ? 'disabled' : ''}`}
      onClick={handleClick}
      disabled={isDisabled ? true : false}
      innerRef={buttonRef}
    >
      {loading ? (
        <>
          <Spinner size="sm" className="me-2">
            Loading...
          </Spinner>
          {t('goalSettings.nextButton')}
        </>
      ) : (
        t('goalSettings.nextButton')
      )}
    </Button>
  );
};

export default NextButton;
